import PropTypes from 'prop-types'
import React, { PureComponent, Fragment } from 'react'
import { withRouter } from '@/checkout-instore-legacy/utils/withRouter'

import { injectIntl, FormattedMessage, defineMessages } from 'react-intl'
import clone from 'lodash/clone'
import isNil from 'lodash/isNil'
import compact from 'lodash/compact'
import last from 'lodash/last'
import isEmpty from 'lodash/isEmpty'
import Spinner from '@vtex/styleguide/lib/Spinner'
import Modal from '@vtex/styleguide/lib/Modal'
import Layout from '@vtex/styleguide/lib/Layout'
import Button from '@vtex/styleguide/lib/Button'

import { intlShape } from '@/checkout-instore-legacy/i18n/intlShape'
import { ROUTES } from '@/checkout-instore-legacy/constants/RoutesConstants'
import Loader from '@/checkout-instore-legacy/components/Commons/Loader'
import SummaryView from '@/checkout-instore-legacy/components/Commons/Summary/containers/SummaryViewContainer'
import QRCodeGenerator from './QRCode/QRCodeGenerator'
import Coupon from '../../components/Commons/Coupon/CouponContainer'
import { Note } from '../../components/Commons/Note/Note'
import FooterButton from '@/checkout-instore-legacy/components/Commons/Elements/FooterButton'
import TopMessage from '@/checkout-instore-legacy/components/Commons/TopMessage'
import Popover from '@/checkout-instore-legacy/components/Commons/Popover'
import InsightsNotificationBar from '@/checkout-instore-legacy/customer/components/InsightsNotificationBar'
import ErrorModal from '@/checkout-instore-legacy/components/Commons/Elements/ErrorModal'
import SocialShareContainer from '@/checkout-instore-legacy/share/containers/SocialShareContainer'
import {
  isClientAnonymous,
  isClientComplete,
  shouldGoToRegisterPage,
} from '@/checkout-instore-legacy/customer/utils'
import {
  changeSelectedSla,
  findBestOtherSlaType,
} from '@/checkout-instore-legacy/shipping/utils'
import { getInstoreConfig } from '@/checkout-instore-legacy/utils/configUtils'
import BarcodeReader from '@/checkout-instore-legacy/utils/BarcodeReader'
import { memoizedDebounce } from '@/checkout-instore-legacy/utils/debounceUtils'
import * as CustomEvent from '@/checkout-instore-legacy/utils/CustomEvent'
import {
  goToPaymentEvent,
  genericImportantError,
  genericImportantEvent,
  logClickEvent,
} from '@/checkout-instore-legacy/utils/EventHelper'
import {
  withSkusMapById,
  willExternalCartRedirect,
  shouldAskUserForAddress,
  groupItemsBySelectedSlaType,
} from '../utils'
import * as CartService from '../service'
import ProductList from './ProductList/ProductList'
import withOrderFormAndVendor from '@/checkout-instore-legacy/order/withOrderFormAndVendor'
import { withMediaQuery } from '@/checkout-instore-legacy/utils/mediaQuery'
import { createProductItemRefManager } from '../message/refManager/ProductItemRefManager'
import { ProductItemRefManagerContext } from '../message/refManager/ProductItemRefManagerContext'
import { startOfOrderMetric } from '../../utils/metricsUtils'

import GiftGroups from './Gifts/GiftGroups'
import ScanIndicator from './ScanIndicator'
import Toolbar from './Toolbar'
import { logChangeSla } from './ProductList/logHelper'
import CartAlertMessage from './CartMessage'
import { ScenarioType } from '../message/types'
import { AmplitudeSidebarLabels } from '@/checkout-instore-legacy/constants/AmplitudeConstants'
import { RequiredInformation } from '@/checkout-instore-legacy/components/RequiredInformation/RequiredInformation'
import ProductSuggestions from '@/checkout-instore-legacy/components/ProductSuggestions'

const messages = defineMessages({
  cartTitle: {
    id: 'cartTitle',
    defaultMessage: '',
  },
  updateCart: {
    id: 'updateCart',
    defaultMessage: '',
  },
  clearCart: {
    id: 'clearCart',
    defaultMessage: '',
  },
})

const EmptyPageHeaderComponent = () => null
const EMPTY_PAGE_HEADER = <EmptyPageHeaderComponent />

const DEFAULT_WAIT_TO_EAN = 300

class Cart extends PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      savedCarts: null,
      giftPickerIsOpen: false,
      showShare: false,
      showCoupon: false,
      showSalesAssociateCode: false,
      showNote: false,
      showRequired: false,
      showTransfer: false,
      productItemRefManager: createProductItemRefManager(),
    }

    this.debouncedAddEanItem = memoizedDebounce(
      this.addEanItem,
      DEFAULT_WAIT_TO_EAN
    )
  }

  hasExternalCartToGo() {
    const { externalCart } = this.props
    return externalCart && (externalCart.change || externalCart.copy)
  }

  handleExternalCart() {
    const { externalCart } = this.props

    const cartService = CartService.getCartService(externalCart)
    if (!cartService) {
      return
    }

    cartService()
      .then(() => {
        this.props.clearExternalCart(externalCart.next)
      })
      .catch((error) => {
        genericImportantError('cart-copy-service', {
          context: 'Internal',
          message: error.message || error.toString(),
        })
        this.props.router.push('/')
      })
  }

  handleCurrentCart() {
    const orderForm = this.props.order.orderForm

    if (!orderForm) {
      const vendor = this.props.vendor
      if (!vendor) {
        this.props.loadInStoreVendor()
      } else {
        this.props.loadInStoreOrderForm(vendor)
      }
    }
  }

  componentDidMount() {
    this.props.clearSearch()
    this.props.clearLogisticsInfoCache()

    if (!this.hasExternalCartToGo()) {
      this.handleCurrentCart()
    } else {
      this.handleExternalCart()
    }

    const {
      order: { orderForm },
    } = this.props
    orderForm && this.props.orderFormUpdate(orderForm)

    BarcodeReader.listen(this.handleBarcodeRead)
    window.handleBarcodeRead = (barcode) =>
      this.handleBarcodeRead({ data: { barcode } }) // used on custom scripts

    CustomEvent.listenEvent('showModal.error', this.handleErrorModal.bind(this))
    CustomEvent.listenEvent(
      'cart.validated',
      this.handleCartValidation.bind(this)
    )
  }

  handleCartValidation(event) {
    const { error, errorMessage } = event.data
    if (errorMessage !== this.props.cartStatus.errorMessage) {
      if (error) {
        this.props.errorCartStatus(errorMessage)
      } else {
        this.props.successCartStatus()
      }
    }
  }

  handleErrorModal(event) {
    this.props.openRootModalWithError({
      errorTitle: event.data.errorTitle,
      error: event.data.error,
      onGiveUp:
        event.data.onGiveUp ||
        (() => {
          this.props.closeModal()
          this.props.router.push('/')
        }),
      onGiveUpText: event.data.onGiveUpText,
      fatal: true,
    })
  }

  componentDidUpdate(prevProps) {
    if (prevProps.items.length < this.props.items.length && this.contentDiv) {
      this.contentDiv.scrollIntoView()
    }

    const previousSelectableGifts = prevProps.orderForm.selectableGifts
    const currentSelectableGifts = this.props.orderForm.selectableGifts

    this.handleNewSelectableGifts(
      previousSelectableGifts,
      currentSelectableGifts
    )

    withSkusMapById(this.props.items).then((items) => {
      this.props.addCartContext(items)
    })

    CustomEvent.sendEvent('cart.change')
  }

  handleNewSelectableGifts(previousGifts, currentGifts) {
    if (
      currentGifts.length !== previousGifts.length &&
      currentGifts.length > 0
    ) {
      const oldIds =
        (previousGifts && previousGifts.map((gift) => gift.id)) || []
      const hasNewMultipleGifts = currentGifts.some(
        (gift) => !oldIds.includes(gift.id) && gift.availableGifts.length > 1
      )

      if (hasNewMultipleGifts) {
        this.setState({ giftPickerIsOpen: true })
      }
    }
  }

  componentWillUnmount() {
    BarcodeReader.unlisten(this.handleBarcodeRead)
    window.handleBarcodeRead = null
    CustomEvent.unlistenEvent('showModal.error', this.handleErrorModal)
    CustomEvent.unlistenEvent('cart.validated', this.handleCartValidation)
  }

  addEanItem = (ean, increment = 1) => {
    this.props.addBarcodeReadItem({ ean }, increment)
  }

  handleBarcodeRead = (event) => {
    let { barcode } = event.data

    if (barcode) {
      barcode = barcode.toString()
    }

    genericImportantEvent('barcode-scanned', {
      context: 'Sale',
      ean: barcode,
    })

    this.debouncedAddEanItem(barcode)
  }

  handleClickGoToPayment(orderForm) {
    if (shouldGoToRegisterPage()) {
      this.props.router.push(ROUTES.CLIENT_REGISTER_PAYMENT_NEXT)
    } else {
      goToPaymentEvent(orderForm)
      this.props.router.push(ROUTES.PAYMENT)
    }
  }

  handleClickGoToShipping(orderForm) {
    const customer = orderForm.clientProfileData

    if (isClientComplete(customer)) {
      this.props.router.push(ROUTES.SHIPPING)
    } else {
      this.props.router.push(ROUTES.CLIENT_REGISTER_SHIPPING_NEXT)
    }
  }

  handleCartProceed = () => {
    const orderForm = this.props.order.orderForm

    const shouldGoToShipping = shouldAskUserForAddress(orderForm)

    startOfOrderMetric()

    return shouldGoToShipping
      ? this.handleClickGoToShipping(orderForm)
      : this.handleClickGoToPayment(orderForm)
  }

  handleCloseGiftPicker = () => {
    logClickEvent('close-gift-picker', 'Sale')
    this.setState({ giftPickerIsOpen: false })
  }

  handleOpenCouponModal = () => {
    logClickEvent('open-coupon-modal', 'Sale')

    this.setState({ showCoupon: true })
  }

  handleCloseCouponModal = () => {
    logClickEvent('close-coupon-modal', 'Sale')

    this.setState({ showCoupon: false })
  }

  handleTransferClick = () => {
    this.setState((prevState) => ({ showTransfer: !prevState.showTransfer }))
  }

  handleOpenSalesAssociateCodeModalClick = () => {
    logClickEvent('open-sales-associate-code-modal', 'Sale')

    if (this.state.showRequired) {
      this.setState({ showRequired: false })
    }

    this.setState({ showSalesAssociateCode: true })
  }

  handleCloseSalesAssociateCodeModal = () => {
    logClickEvent('close-sales-associate-code-modal', 'Sale')

    this.setState({ showSalesAssociateCode: false })
  }

  handleOpenNoteModal = () => {
    logClickEvent('open-note-modal', 'Sale')

    if (this.state.showRequired) {
      this.setState({ showRequired: false })
    }

    this.setState({ showNote: true })
  }

  handleCloseNoteModal = () => {
    logClickEvent('close-note-modal', 'Sale')

    this.setState({ showNote: false })
  }

  handleOpenRequiredInformation = () => {
    logClickEvent('open-required-information-modal', 'Sale')

    this.setState({ showRequired: true, showShare: false })
  }

  handleCloseRequiredInformation = () => {
    logClickEvent('close-required-information-modal', 'Sale')

    this.setState({ showRequired: false })
  }

  handlePickGifts = () => {
    logClickEvent('choose-gift', 'Sale')
    this.setState({ giftPickerIsOpen: true })
    this.props.userPickedGifts()
  }

  handleOpenShare = () => {
    logClickEvent('open-social-share', 'Sale')
    const { noteAsVendorCode } = getInstoreConfig()
    const { order } = this.props
    const { orderForm } = order
    const { openTextField } = orderForm
    if (
      !isEmpty(noteAsVendorCode) &&
      (isEmpty(openTextField) || isEmpty(openTextField.value))
    ) {
      this.handleOpenRequiredInformation()
      return
    }

    this.setState({ showShare: true })
  }

  handleCloseShare = () => {
    logClickEvent('close-social-share', 'Sale')

    this.setState({ showShare: false })
  }

  handleChangeSla = (item) => {
    if (isNil(item) || isNil(item.index)) {
      return
    }

    logChangeSla(item)

    const orderForm = this.props.order.orderForm
    if (!orderForm) {
      return
    }

    const { index, inHands } = item
    const { shippingData } = orderForm
    if (!shippingData.logisticsInfo[index]) {
      return
    }

    const logisticsInfo = clone(shippingData.logisticsInfo)
    const sla = findBestOtherSlaType(orderForm, index)

    if (!sla && !inHands) {
      this.props.askForceInHandsItem(item)
      return
    }

    if (inHands) {
      this.props.changeItemToRemoveInHands(item)
      return
    }

    logisticsInfo[index] = changeSelectedSla(logisticsInfo[index], sla)

    this.props.sendLogisticsInfoOnShippingData(
      logisticsInfo,
      logisticsInfo[index].itemId
    )
  }

  checkIfHasGifts = (orderForm) => {
    return (
      orderForm &&
      orderForm.selectableGifts &&
      orderForm.selectableGifts.length > 0 &&
      orderForm.selectableGifts.some(
        (gift) => gift.availableGifts && gift.availableGifts.length > 0
      )
    )
  }

  renderFooterButton = () => {
    const { order, hasAtLeastOneValidItem, isOnline } = this.props
    const { noteAsVendorCode } = getInstoreConfig()
    const { error: invalidCart } = this.props.cartStatus
    const { orderForm } = order
    const orderFormHasItems = orderForm.items.length > 0
    const { openTextField } = orderForm

    const hasErrors = this.haveCartPendencies()

    const hasItems = this.props.items.length > 0
    const userAlreadyPickedGifts = order.userAlreadyPickedGifts
    if ((!hasItems && !orderFormHasItems) || !hasAtLeastOneValidItem) {
      return null
    }

    const hasGifts = this.checkIfHasGifts(orderForm)

    const shouldGoToShipping = shouldAskUserForAddress(orderForm)

    const footerButtonDisabled =
      !isOnline ||
      hasErrors ||
      invalidCart ||
      (!shouldGoToShipping && this.props.waitingForGiftCardArrival)

    if (
      !isEmpty(noteAsVendorCode) &&
      (isEmpty(openTextField) || isEmpty(openTextField.value))
    ) {
      return (
        <FooterButton
          id="go-to-vendor-code"
          testId="go-to-vendor-code"
          variation="primary"
          disabled={footerButtonDisabled}
          isLoading={this.props.isLoading}
          onClick={this.handleOpenNoteModal}
          className="absolute-l bottom-0-l right-0-l w-25-l lh-copy-l"
        >
          <FormattedMessage id="cartGoToVendorCode" />
        </FooterButton>
      )
    }

    if (!hasGifts || userAlreadyPickedGifts) {
      return (
        <FooterButton
          id="go-to-payment"
          variation="primary"
          disabled={footerButtonDisabled}
          isLoading={this.props.isLoading}
          onClick={this.handleCartProceed}
          className="absolute-l bottom-0-l right-0-l w-25-l lh-copy-l"
        >
          <FormattedMessage
            id={shouldGoToShipping ? 'cartGoToShipping' : 'cartGoToPayment'}
          />
        </FooterButton>
      )
    }

    return (
      <FooterButton
        id="cart-choose-gifts"
        kind="primary"
        disabled={hasErrors || !isOnline}
        onClick={this.handlePickGifts}
        className="absolute-l bottom-0-l right-0-l w-25-l lh-copy-l"
      >
        <FormattedMessage id="chooseGifts" />
      </FooterButton>
    )
  }

  getProductList = (items, titleIntlId, key) => {
    const { intl, router, cartMessageMap } = this.props

    return (
      items.length > 0 && (
        <ProductList
          productItemRefManager={this.state.productItemRefManager}
          listId={key}
          editable
          key={key}
          router={router}
          items={items}
          cartMessageMap={cartMessageMap}
          isLoading={this.props.isLoading}
          title={intl.formatMessage({
            id: titleIntlId,
          })}
          onChangeSla={this.handleChangeSla}
        />
      )
    )
  }

  handleBuyWithoutStockContinue = () => {
    const item = this.props.askForceInHandsItemState
    this.props.changeItemForceInHands(item)
    this.props.clearAskForceInHandsItem()
  }

  handleBuyWithoutStockCancel = () => {
    this.props.clearAskForceInHandsItem()
  }

  renderHeaderTitle = (title) => {
    const {
      intl: { formatMessage },
      clearCart,
      updateCart,
      mediaQuery,
      isLoading,
    } = this.props

    const isMobile = mediaQuery({
      phone: true,
      tablet: false,
      desktop: false,
    })

    const buttonSize = isMobile ? 'small' : 'regular'

    return (
      <div
        className={
          'c-on-base flex flex-wrap flex-column flex-row-ns justify-between mv6 mv7-ns'
        }
      >
        <div className="t-heading-4 t-heading-2-ns order-0 flex-grow-1 ml5 ml1-ns lh-title b mb3 mb0-ns">
          {title}
        </div>
        <div className="flex justify-end-ns lh-solid ml5 ">
          <Button
            variation="tertiary"
            collapseLeft
            size={buttonSize}
            testId="update-cart"
            disabled={isLoading}
            onClick={updateCart}
          >
            {formatMessage(messages.updateCart)}
          </Button>
          <span className="pr7" />
          <Button
            variation="tertiary"
            collapseLeft
            size={buttonSize}
            testId="clear-cart"
            disabled={isLoading}
            onClick={clearCart}
          >
            {formatMessage(messages.clearCart)}
          </Button>
        </div>
      </div>
    )
  }

  getLastValidSku = (skuItems) => {
    const items = compact(skuItems)
    if (isEmpty(items)) {
      return null
    }
    return last(items)
  }

  generateContent = (hasItems) => {
    if (!hasItems) {
      const { profile, openCart, router, cartMessages } = this.props

      const showProfile = !isClientAnonymous(profile)
      const showInsights =
        showProfile || (openCart && openCart.customerIdentification !== null)

      return (
        <div className="vh-75 flex-auto">
          <div>
            {showInsights && (
              <InsightsNotificationBar
                profile={profile}
                router={router}
                openCart={openCart}
              />
            )}
            {!isEmpty(cartMessages) && (
              <div data-testid="cart-alert-message-list">
                {cartMessages.map((cartMessage, index) => (
                  <div key={index} data-testid={`cart-alert-message-${index}`}>
                    <CartAlertMessage cartMessage={cartMessage} />
                  </div>
                ))}
              </div>
            )}
          </div>
          {getInstoreConfig().productSuggestionsEnabled ? (
            <ProductSuggestions />
          ) : (
            <div
              className={`mt5 mt6-ns h-100 flex justify-center items-center`}
            >
              <ScanIndicator />
            </div>
          )}
        </div>
      )
    }

    if (hasItems) {
      const {
        intl: { formatMessage },
        cartMessages,
      } = this.props

      const items = this.props.items.filter((item) => item.quantity !== 0)

      const groups = groupItemsBySelectedSlaType(items)
      const pendingCart = this.getProductList(
        groups.pending,
        'cartTitlePending',
        'PendingCart'
      )
      const takeAwayCart = this.getProductList(
        groups.takeAway,
        'cartTitleTakeAway',
        'TakeAwayCart'
      )
      const deliveryCart = this.getProductList(
        groups.delivery,
        'cartTitleDelivery',
        'DeliveryCart'
      )

      return (
        <Fragment>
          {this.renderHeaderTitle(formatMessage(messages.cartTitle))}
          <ProductItemRefManagerContext.Provider
            value={this.state.productItemRefManager}
          >
            {!isEmpty(cartMessages) && (
              <div data-testid="cart-alert-message-list">
                {cartMessages.map((cartMessage, index) => (
                  <CartAlertMessage key={index} cartMessage={cartMessage} />
                ))}
              </div>
            )}
            <div data-cy="product-lists">
              {pendingCart}
              {takeAwayCart}
              {deliveryCart}
            </div>
          </ProductItemRefManagerContext.Provider>
        </Fragment>
      )
    }
  }

  haveCartPendencies = () => {
    const { cartMessages } = this.props
    const pendencies = cartMessages.filter(
      (cartMessage) => cartMessage.scenario === ScenarioType.PENDENCY
    )
    return !isEmpty(pendencies)
  }

  render() {
    const {
      giftPickerIsOpen,
      showShare,
      showCoupon,
      showSalesAssociateCode,
      showNote,
      showTransfer,
      showRequired,
    } = this.state
    const { error: invalidCart, errorMessage } = this.props.cartStatus
    const {
      cartCode,
      queuedIncrements,
      order,
      askForceInHandsItemState,
    } = this.props

    const orderForm = order.orderForm
    const noteValue =
      (orderForm.openTextField && orderForm.openTextField.value) || ''
    const salesAssociateId =
      orderForm?.merchantContextData?.salesAssociateData?.salesAssociateId

    const loading = order.loading || !orderForm
    if (!orderForm) {
      if (order.error) {
        return null
      }

      return <Loader loading />
    }

    if (willExternalCartRedirect(this.props.externalCart)) {
      return <Loader loading />
    }

    const hasItems = this.props.items.length > 0

    const content = (
      <Layout fullWidth pageHeader={EMPTY_PAGE_HEADER}>
        {this.generateContent(hasItems)}
      </Layout>
    )

    const hasGifts = this.checkIfHasGifts(orderForm)

    return (
      <Fragment>
        <ErrorModal
          errorIntl={'withoutStock'}
          isOpen={!!askForceInHandsItemState}
          onContinue={this.handleBuyWithoutStockContinue}
          onCancel={this.handleBuyWithoutStockCancel}
          confirmButtonDataCy="force-in-hands-confirm-button"
          cancelButtonDataCy="force-in-hands-cancel-button"
        />
        <SummaryView renderFooterButton={this.renderFooterButton}>
          <div className="flex flex-auto flex-column">
            <Loader loading={loading} />
            <div className="flex-auto flex flex-column">
              <div className="flex-none">
                <Toolbar
                  hasItems={hasItems}
                  hasGifts={hasGifts}
                  selectableGifts={orderForm.selectableGifts}
                  onShareClick={this.handleOpenShare}
                  onGiftClick={this.handlePickGifts}
                  onCouponClick={this.handleOpenCouponModal}
                  onTransferClick={this.handleTransferClick}
                  onSalesAssociateCodeClick={
                    this.handleOpenSalesAssociateCodeModalClick
                  }
                  onNoteClick={this.handleOpenNoteModal}
                  salesAssociateCode={salesAssociateId}
                  noteValue={noteValue}
                  hasCoupon={
                    orderForm.marketingData && orderForm.marketingData.coupon
                  }
                />
              </div>

              {invalidCart && errorMessage ? (
                <div className="flex-none">
                  <TopMessage isVisible message={errorMessage} type="error" />
                </div>
              ) : null}

              <div
                className={`flex-auto bg-light-silver overflow-auto flex-column momentum relative ${
                  hasItems ? '' : 'flex'
                }`}
              >
                <div
                  className="w-100 flex flex-column flex-auto"
                  ref={(div) => (this.contentDiv = div)}
                >
                  {content}
                </div>
              </div>
            </div>
          </div>
        </SummaryView>

        <Modal
          centered
          isOpen={
            giftPickerIsOpen &&
            orderForm.selectableGifts &&
            orderForm.selectableGifts.length > 0
          }
          onClose={this.handleCloseGiftPicker}
        >
          <GiftGroups
            selectableGifts={orderForm.selectableGifts}
            isLoading={loading}
            onClose={this.handleCloseGiftPicker}
          />
        </Modal>

        <Modal centered isOpen={showShare} onClose={this.handleCloseShare}>
          <SocialShareContainer orderForm={orderForm} />
        </Modal>

        <Modal
          centered
          isOpen={showCoupon}
          onClose={this.handleCloseCouponModal}
        >
          <Coupon
            value={
              orderForm.marketingData && orderForm.marketingData.coupon
                ? orderForm.marketingData.coupon
                : ''
            }
            onClose={this.handleCloseCouponModal}
            isOpen={showCoupon}
            isLoading={loading}
          />
        </Modal>

        <RequiredInformation
          content="requiredVendorCodeMessage"
          title="requiredVendorCodeTitle"
          isOpen={showRequired}
          onClose={this.handleCloseRequiredInformation}
          onFillActionClick={this.handleOpenNoteModal}
        />

        <Note
          value={salesAssociateId}
          onClose={this.handleCloseSalesAssociateCodeModal}
          isOpen={showSalesAssociateCode}
        />

        <Note
          value={noteValue}
          onClose={this.handleCloseNoteModal}
          isOpen={showNote}
          isOrderNote
        />

        <Popover
          visible={showTransfer}
          onClose={() => this.setState({ showTransfer: false })}
          offsetY={-10}
          source={document.querySelector('#transferShow')}
        >
          <div className="flex items-center justify-center align-center pb5 pt5 flex-column">
            <QRCodeGenerator size={200} />
            {cartCode !== null ? (
              <React.Fragment>
                <div className="db pt4" id="cart-transfer-qr">
                  <FormattedMessage id="or" />
                </div>
                <div className="db f1 c-action-primary" id="cart-transfer-code">
                  {cartCode}
                </div>
              </React.Fragment>
            ) : queuedIncrements === 0 ? (
              <div className="pb4" />
            ) : (
              <div className="pt4">
                <Spinner />
              </div>
            )}
            <div className="gray pl4 pr4 f6 tc">
              <FormattedMessage id="cartTransferTip" />
            </div>
          </div>
        </Popover>
      </Fragment>
    )
  }
}

Cart.propTypes = {
  router: PropTypes.any,
  addCartContext: PropTypes.func.isRequired,
  clearExternalCart: PropTypes.func.isRequired,
  externalCart: PropTypes.shape({
    copy: PropTypes.string,
    change: PropTypes.string,
    next: PropTypes.string,
  }).isRequired,
  profile: PropTypes.object,
  intl: intlShape.isRequired,
  cartStatus: PropTypes.shape({
    error: PropTypes.bool,
    errorMessage: PropTypes.string,
  }),
  successCartStatus: PropTypes.func.isRequired,
  errorCartStatus: PropTypes.func.isRequired,
  cartCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  queuedIncrements: PropTypes.number,
  openCart: PropTypes.object,
  sendLogisticsInfoOnShippingData: PropTypes.func,
  changeItemForceInHands: PropTypes.func.isRequired,
  askForceInHandsItem: PropTypes.func.isRequired,
  changeItemToRemoveInHands: PropTypes.func,
  order: PropTypes.object.isRequired,
  loadInStoreOrderForm: PropTypes.func.isRequired,
  userPickedGifts: PropTypes.func.isRequired,
  loadInStoreVendor: PropTypes.func.isRequired,
  vendor: PropTypes.object.isRequired,
  items: PropTypes.array.isRequired,
  clearAskForceInHandsItem: PropTypes.func.isRequired,
  askForceInHandsItemState: PropTypes.object,
  isLoading: PropTypes.bool,
  orderFormUpdate: PropTypes.func.isRequired,
  addItem: PropTypes.func.isRequired,
  openRootModalWithError: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  dismissRootModal: PropTypes.func,
  clearSearch: PropTypes.func,
  waitingForGiftCardArrival: PropTypes.bool,
  hasAtLeastOneValidItem: PropTypes.bool,
  addBarcodeReadItem: PropTypes.func.isRequired,
  clearCart: PropTypes.func.isRequired,
  updateCart: PropTypes.func.isRequired,
  cartMessages: PropTypes.array.isRequired,
  cartMessageMap: PropTypes.object.isRequired,
  clearLogisticsInfoCache: PropTypes.func.isRequired,
  updateCartAddressWithSimulationAddress: PropTypes.func.isRequired,
  isOnline: PropTypes.bool,
}

Cart.defaultProps = {
  isOnline: true,
}

export default injectIntl(
  withOrderFormAndVendor(withRouter(withMediaQuery(Cart)))
)
